import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useQuery } from '@tanstack/react-query';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { usePanel } from 'contexts/panelContext';
import { userFilesApi } from 'api';
import { SessionFile } from 'types/SessionFile';
import { RouterUrlParams } from 'App';
import { BreadCrumb } from 'primereact/breadcrumb';
import { usePair } from 'contexts/pairContext';
import { DownloadMaterialsButton } from 'components/userPanel/processSummary/DownloadMaterialsButton';
import { Button } from 'components/_new/Button';
import { NewUploadUserFilesDialog } from 'components/session/yourNotes/NewUploadUserFilesDialog';
import PairPartnerInfo from '../components/PairPartnerInfo';
import { NoteList } from './NoteList';

export const Browse = () => {
	const { t } = useTranslation();
	const { organizationName, programName, type } = useParams() as RouterUrlParams;
	const navigate = useNavigate();

	const { currentProgramMembership, currentApplicationData } = usePanel();
	const {
		pair: { id: pairId },
	} = usePair();

	const {
		data: getUserfilesData,
		refetch,
		isLoading: getUserfilesLoading,
		isSuccess: getUserfilesSuccess,
	} = useQuery(
		[
			'userfiles-list',
			{ programMembership: currentProgramMembership?.id, application: currentApplicationData?.id, pairId },
		],
		() =>
			userFilesApi.getUserfiles(
				Number(currentProgramMembership?.id),
				Number(currentApplicationData?.id),
				pairId,
				{}
			),
		{
			enabled: Boolean(currentProgramMembership?.id) && Boolean(currentApplicationData?.id),
		}
	);
	const files: SessionFile[] = getUserfilesData || [];

	const homeBreadcrumb = {
		icon: 'pi pi-book',
		command: () => {
			navigate(`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/userfiles`);
		},
	};

	const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);

	return (
		<>
			<Helmet title={t('userPanel.userFiles.title')} />
			<SubPageTitle
				title={
					<div className="flex flex-column">
						{t('userPanel.userFiles.title')}
						<PairPartnerInfo />
					</div>
				}
				right={
					<div className="flex flex-row gap-4 justify-content-betwen align-items-center">
						<DownloadMaterialsButton />
						<Button
							icon="plus"
							iconStyle="solid"
							label={t('actions.add')}
							onClick={() => {
								setAddNoteDialogOpen(true);
							}}
						/>
					</div>
				}
			/>
			<NewUploadUserFilesDialog
				visible={addNoteDialogOpen}
				onHide={() => {
					setAddNoteDialogOpen(false);
					refetch();
				}}
				onFinish={() => {
					refetch();
				}}
				onSave={() => {
					refetch();
				}}
			/>
			<BreadCrumb model={[{ label: t('userPanel.userFiles.title') }]} home={homeBreadcrumb} className="mb-3" />

			<p>{t('userPanel.userFiles.desc')}</p>

			<NoteList
				items={files}
				onUpdate={() => {
					refetch();
				}}
				isLoading={getUserfilesLoading}
				displaySessionNumberInfo
			/>
		</>
	);
};
